import {
  ApiOutlined,
  CheckCircleFilled,
  ClockCircleOutlined,
  CloudServerOutlined,
  CopyOutlined,
  DatabaseOutlined,
  ReloadOutlined,
  RobotOutlined,
  SaveOutlined,
  SettingOutlined
} from "@ant-design/icons";
import {
  Alert,
  App,
  Badge,
  Button,
  Collapse,
  Descriptions,
  Drawer,
  Form,
  Input,
  InputNumber,
  Space,
  Tag,
  Tooltip
} from "antd";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  checkHealth,
  getConnectionSettings,
  normalizeApiBase,
  saveConnectionSettings,
  type ConnectionSettings,
  type HealthCheckResult
} from "../services/api";

type HealthPhase = "checking" | "online" | "degraded" | "offline";
type ServiceDetail = "api" | "database" | "easybot" | "game";
type EasyBotDeploymentSettings = {
  apiBase: string;
  timeoutSeconds: number;
  pollIntervalSeconds: number;
};

type HealthState = {
  phase: HealthPhase;
  result: HealthCheckResult | null;
  error: string | null;
};

const initialHealth: HealthState = {
  phase: "checking",
  result: null,
  error: null
};

const EASYBOT_DEPLOYMENT_SETTINGS_KEY = "navigation-plan-easybot-deployment-settings";
const defaultEasyBotDeploymentSettings: EasyBotDeploymentSettings = {
  apiBase: "",
  timeoutSeconds: 8,
  pollIntervalSeconds: 15
};

function getEasyBotDeploymentSettings(): EasyBotDeploymentSettings {
  try {
    const saved = JSON.parse(
      localStorage.getItem(EASYBOT_DEPLOYMENT_SETTINGS_KEY) ?? "{}"
    ) as Partial<EasyBotDeploymentSettings>;
    return {
      apiBase: typeof saved.apiBase === "string"
        ? normalizeApiBase(saved.apiBase)
        : defaultEasyBotDeploymentSettings.apiBase,
      timeoutSeconds: typeof saved.timeoutSeconds === "number"
        ? Math.min(30, Math.max(1, saved.timeoutSeconds))
        : defaultEasyBotDeploymentSettings.timeoutSeconds,
      pollIntervalSeconds: typeof saved.pollIntervalSeconds === "number"
        ? Math.min(120, Math.max(5, saved.pollIntervalSeconds))
        : defaultEasyBotDeploymentSettings.pollIntervalSeconds
    };
  } catch {
    return defaultEasyBotDeploymentSettings;
  }
}

function saveEasyBotDeploymentSettings(settings: EasyBotDeploymentSettings) {
  const normalized: EasyBotDeploymentSettings = {
    apiBase: normalizeApiBase(settings.apiBase),
    timeoutSeconds: Math.min(30, Math.max(1, settings.timeoutSeconds)),
    pollIntervalSeconds: Math.min(120, Math.max(5, settings.pollIntervalSeconds))
  };
  localStorage.setItem(EASYBOT_DEPLOYMENT_SETTINGS_KEY, JSON.stringify(normalized));
  return normalized;
}

function displayApiBase(apiBase: string) {
  return apiBase || `${window.location.origin}（同源代理）`;
}

function formatUptime(seconds?: number) {
  if (seconds === undefined) return "—";
  if (seconds < 60) return `${seconds} 秒`;
  if (seconds < 3_600) return `${Math.floor(seconds / 60)} 分钟`;
  if (seconds < 86_400) return `${Math.floor(seconds / 3_600)} 小时`;
  return `${Math.floor(seconds / 86_400)} 天 ${Math.floor((seconds % 86_400) / 3_600)} 小时`;
}

function formatHeartbeatAge(ageMs: number | null | undefined) {
  if (ageMs === null || ageMs === undefined) return "尚未收到";
  if (ageMs < 1_000) return "刚刚";
  if (ageMs < 60_000) return `${Math.max(1, Math.floor(ageMs / 1_000))} 秒前`;
  return `${Math.floor(ageMs / 60_000)} 分钟前`;
}

export function ConnectionStatus({ login = false }: { login?: boolean }) {
  const { message } = App.useApp();
  const [settings, setSettings] = useState<ConnectionSettings>(getConnectionSettings);
  const [health, setHealth] = useState<HealthState>(initialHealth);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [serviceDetail, setServiceDetail] = useState<ServiceDetail | null>(null);
  const [easyBotDeployment, setEasyBotDeployment] = useState<EasyBotDeploymentSettings>(
    getEasyBotDeploymentSettings
  );
  const [testing, setTesting] = useState(false);
  const [easyBotTesting, setEasyBotTesting] = useState(false);
  const [form] = Form.useForm<ConnectionSettings>();
  const [easyBotForm] = Form.useForm<EasyBotDeploymentSettings>();

  const runCheck = useCallback(async (
    candidate: ConnectionSettings,
    isManual = false
  ) => {
    if (isManual) setTesting(true);
    setHealth((current) => ({ ...current, phase: "checking", error: null }));
    try {
      const result = await checkHealth(candidate);
      const coreOnline = result.report.ok && result.report.database.ok;
      const easyBotOnline = result.report.integrations.easybot.connected;
      const gameOnline = result.report.integrations.game.connected;
      setHealth({
        phase: coreOnline && easyBotOnline && gameOnline ? "online" : "degraded",
        result,
        error: null
      });
      if (isManual) {
        if (coreOnline && easyBotOnline && gameOnline) {
          void message.success(`连接成功，往返 ${result.roundTripMs}ms`);
        } else if (coreOnline) {
          void message.warning("API 与 MySQL 正常，但 EasyBot 或游戏桥接尚未连接");
        } else {
          void message.warning("API 可访问，但数据库连接异常");
        }
      }
    } catch (error) {
      const detail = error instanceof Error
        ? error.name === "AbortError" || error.name === "TimeoutError"
          ? `连接超时（${candidate.timeoutSeconds} 秒）`
          : error.message
        : "无法连接服务";
      setHealth({ phase: "offline", result: null, error: detail });
      if (isManual) void message.error(detail);
    } finally {
      if (isManual) setTesting(false);
    }
  }, [message]);

  useEffect(() => {
    void runCheck(settings);
    const timer = window.setInterval(
      () => void runCheck(settings),
      settings.pollIntervalSeconds * 1_000
    );
    return () => window.clearInterval(timer);
  }, [runCheck, settings]);

  useEffect(() => {
    if (serviceDetail === "api") form.setFieldsValue(settings);
  }, [form, serviceDetail, settings]);

  useEffect(() => {
    if (serviceDetail === "easybot") easyBotForm.setFieldsValue(easyBotDeployment);
  }, [easyBotDeployment, easyBotForm, serviceDetail]);

  const statusPresentation = useMemo(() => {
    if (health.phase === "checking") {
      return { badge: "processing" as const, label: "正在检测", detail: "正在获取实时状态" };
    }
    if (health.phase === "online") {
      return {
        badge: "success" as const,
        label: `服务在线 · ${health.result?.roundTripMs ?? 0}ms`,
        detail: "API、MySQL 与 EasyBot 运行正常"
      };
    }
    if (health.phase === "degraded") {
      if (health.result?.report.database.ok === false) {
        return { badge: "warning" as const, label: "数据库异常", detail: "API 在线，MySQL 不可用" };
      }
      return {
        badge: "warning" as const,
        label: "EasyBot 未连接",
        detail: "API 与 MySQL 正常，但没有收到 EasyBot 实时心跳"
      };
    }
    return { badge: "error" as const, label: "连接失败", detail: health.error ?? "服务不可访问" };
  }, [health]);

  const openSettings = () => {
    setServiceDetail(null);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setServiceDetail(null);
    setDrawerOpen(false);
  };

  const testDraft = async () => {
    try {
      const values = await form.validateFields();
      await runCheck({
        ...values,
        apiBase: normalizeApiBase(values.apiBase)
      }, true);
    } catch (error) {
      if (error instanceof Error) void message.error(error.message);
    }
  };

  const saveDraft = async () => {
    try {
      const values = await form.validateFields();
      const saved = saveConnectionSettings(values);
      setSettings(saved);
      void message.success("连接设置已保存并应用");
    } catch (error) {
      if (error instanceof Error) void message.error(error.message);
    }
  };

  const readEasyBotDeploymentDraft = async () => {
    const values = await easyBotForm.validateFields();
    return {
      ...values,
      apiBase: normalizeApiBase(values.apiBase)
    };
  };

  const testEasyBotDeployment = async () => {
    try {
      setEasyBotTesting(true);
      const values = await readEasyBotDeploymentDraft();
      const result = await checkHealth({
        apiBase: values.apiBase,
        pollIntervalSeconds: values.pollIntervalSeconds,
        timeoutSeconds: values.timeoutSeconds
      });
      if (!result.report.api.ok || !result.report.database.ok) {
        void message.warning("目标 API 可访问，但服务或 MySQL 状态异常");
        return;
      }
      void message.success(`目标 API 可访问，往返 ${result.roundTripMs}ms`);
    } catch (error) {
      if (error instanceof Error) void message.error(error.message);
    } finally {
      setEasyBotTesting(false);
    }
  };

  const saveEasyBotDeployment = async () => {
    try {
      const saved = saveEasyBotDeploymentSettings(await readEasyBotDeploymentDraft());
      setEasyBotDeployment(saved);
      void message.success("EasyBot 跨服务器部署参数已保存");
    } catch (error) {
      if (error instanceof Error) void message.error(error.message);
    }
  };

  const copyEasyBotDeployment = async () => {
    try {
      const values = await readEasyBotDeploymentDraft();
      const configuration = [
        `api_base=${values.apiBase}`,
        `timeout_ms=${values.timeoutSeconds * 1_000}`,
        `action_poll_ms=${values.pollIntervalSeconds * 1_000}`,
        "service_token=<填写与 API 的 INTERNAL_SERVICE_TOKEN 相同的值>"
      ].join("\n");
      await navigator.clipboard.writeText(configuration);
      void message.success("EasyBot 配置值已复制");
    } catch (error) {
      if (error instanceof Error) void message.error(error.message);
    }
  };

  const trigger = login ? (
    <Button
      type="text"
      size="small"
      icon={<SettingOutlined />}
      className="login-connection-button"
      onClick={openSettings}
    >
      连接设置
    </Button>
  ) : (
    <Tooltip title={`${statusPresentation.detail}，点击查看或设置连接`}>
      <button type="button" className="connection-status-button" onClick={openSettings}>
        <Badge status={statusPresentation.badge} />
        <b>{statusPresentation.label}</b>
        <SettingOutlined />
      </button>
    </Tooltip>
  );

  const apiOnline = health.result?.report.api.ok === true;
  const databaseOnline = health.result?.report.database.ok === true;
  const easyBot = health.result?.report.integrations?.easybot;
  const easyBotOnline = easyBot?.connected === true;
  const game = health.result?.report.integrations?.game;
  const gameOnline = game?.connected === true;
  const gameMetadata = game?.metadata ?? {};
  const lastChecked = health.result
    ? dayjs(health.result.checkedAt).format("YYYY-MM-DD HH:mm:ss")
    : health.error ?? "尚未完成检测";

  return (
    <>
      {trigger}
      <Drawer
        title={(
          <Space>
            <CloudServerOutlined />
            <span>服务连接与实时状态</span>
          </Space>
        )}
        size={520}
        open={drawerOpen}
        onClose={closeDrawer}
        extra={<Tag color={statusPresentation.badge}>{statusPresentation.label}</Tag>}
      >
        <section className={`connection-summary connection-summary--${health.phase}`}>
          <div className="connection-summary__icon">
            {health.phase === "online" ? <CheckCircleFilled /> : <CloudServerOutlined />}
          </div>
          <div>
            <strong>{statusPresentation.label}</strong>
            <p>{statusPresentation.detail}</p>
          </div>
        </section>

        <Collapse
          className="connection-services connection-service-collapse"
          accordion
          expandIconPlacement="end"
          activeKey={serviceDetail ?? undefined}
          onChange={(key) => {
            const nextKey = Array.isArray(key) ? key[0] : key;
            setServiceDetail(nextKey ? nextKey as ServiceDetail : null);
          }}
          items={[
            {
              key: "api",
              forceRender: true,
              label: (
                <span className="connection-service-label">
                  <ApiOutlined />
                  <strong>API 服务</strong>
                </span>
              ),
              extra: (
                <Tag color={apiOnline ? "success" : health.phase === "checking" ? "processing" : "error"}>
                  {apiOnline ? "正常" : health.phase === "checking" ? "检测中" : "不可用"}
                </Tag>
              ),
              children: (
                <div className="connection-service-panel" data-service-panel="api">
                  <Descriptions
                    className="connection-details connection-details--service"
                    bordered
                    size="small"
                    column={1}
                    items={[
                      {
                        key: "status",
                        label: "实时状态",
                        children: <Badge status={apiOnline ? "success" : "error"} text={apiOnline ? "运行正常" : "不可用"} />
                      },
                      {
                        key: "address",
                        label: "当前地址",
                        children: <code>{displayApiBase(settings.apiBase)}</code>
                      },
                      {
                        key: "round-trip",
                        label: "请求往返",
                        children: health.result ? `${health.result.roundTripMs}ms` : "—"
                      },
                      {
                        key: "checked",
                        label: "最后检测",
                        children: lastChecked
                      },
                      {
                        key: "uptime",
                        label: "运行时间",
                        children: formatUptime(health.result?.report.uptimeSeconds)
                      }
                    ]}
                  />

                  <div className="connection-form-heading">
                    <div>
                      <strong>连接设置</strong>
                      <p>设置浏览器连接的 API 地址、端口与检测频率。</p>
                    </div>
                  </div>

                  <Form<ConnectionSettings>
                    form={form}
                    layout="vertical"
                    initialValues={settings}
                    requiredMark={false}
                  >
                    <Form.Item
                      name="apiBase"
                      label="API 地址（IP / 域名 + 端口）"
                      extra="留空时使用当前网页的同源 /api 与 /health 代理。"
                      rules={[
                        {
                          validator: async (_, value: string) => {
                            try {
                              normalizeApiBase(value ?? "");
                            } catch (error) {
                              throw new Error(error instanceof Error ? error.message : "API 地址格式不正确");
                            }
                          }
                        }
                      ]}
                    >
                      <Input
                        prefix={<CloudServerOutlined />}
                        placeholder="http://127.0.0.1:3210"
                        allowClear
                      />
                    </Form.Item>
                    <div className="connection-number-grid">
                      <Form.Item
                        name="pollIntervalSeconds"
                        label="检测间隔"
                        rules={[{ required: true, message: "请输入检测间隔" }]}
                      >
                        <InputNumber min={5} max={120} suffix="秒" style={{ width: "100%" }} />
                      </Form.Item>
                      <Form.Item
                        name="timeoutSeconds"
                        label="连接超时"
                        rules={[{ required: true, message: "请输入超时时间" }]}
                      >
                        <InputNumber min={1} max={15} suffix="秒" style={{ width: "100%" }} />
                      </Form.Item>
                    </div>
                    <div className="connection-form-actions">
                      <Button
                        icon={<ReloadOutlined />}
                        loading={testing}
                        onClick={() => void testDraft()}
                      >
                        测试连接
                      </Button>
                      <Button
                        type="primary"
                        icon={<SaveOutlined />}
                        onClick={() => void saveDraft()}
                      >
                        保存并应用
                      </Button>
                    </div>
                    <p className="connection-form-note">
                      <ClockCircleOutlined /> 设置仅保存在当前浏览器，不会写入服务器密钥或数据库密码。
                    </p>
                  </Form>
                </div>
              )
            },
            {
              key: "database",
              label: (
                <span className="connection-service-label">
                  <DatabaseOutlined />
                  <strong>MySQL 数据库</strong>
                </span>
              ),
              extra: (
                <Tag color={databaseOnline ? "success" : health.phase === "checking" ? "processing" : "error"}>
                  {databaseOnline
                    ? health.result?.report.database.latencyMs === null
                      ? "正常 · 未返回延迟"
                      : `正常 · ${health.result?.report.database.latencyMs}ms`
                    : health.phase === "checking" ? "检测中" : "不可用"}
                </Tag>
              ),
              children: (
                <div className="connection-service-panel" data-service-panel="database">
                  <Descriptions
                    className="connection-details connection-details--service"
                    bordered
                    size="small"
                    column={1}
                    items={[
                      {
                        key: "status",
                        label: "实时状态",
                        children: (
                          <Badge
                            status={databaseOnline ? "success" : "error"}
                            text={databaseOnline ? "连接正常" : "连接异常"}
                          />
                        )
                      },
                      {
                        key: "latency",
                        label: "查询延迟",
                        children: health.result?.report.database.latencyMs === null
                          || health.result?.report.database.latencyMs === undefined
                          ? "—"
                          : `${health.result.report.database.latencyMs}ms`
                      },
                      {
                        key: "checked",
                        label: "最后检测",
                        children: lastChecked
                      },
                      {
                        key: "source",
                        label: "数据来源",
                        children: "服务端 MySQL 实时健康检查"
                      }
                    ]}
                  />
                  <div className="connection-detail-note">
                    <DatabaseOutlined />
                    <div>
                      <strong>数据库连接由服务端管理</strong>
                      <p>连接地址与账号由 API 服务的 DATABASE_URL 配置提供，浏览器不会读取或暴露数据库凭据。</p>
                    </div>
                  </div>
                </div>
              )
            },
            {
              key: "easybot",
              label: (
                <span className="connection-service-label">
                  <RobotOutlined />
                  <strong>EasyBot 机器人</strong>
                </span>
              ),
              extra: (
                <Tag color={easyBotOnline ? "success" : health.phase === "checking" ? "processing" : "error"}>
                  {health.phase === "checking"
                    ? "检测中"
                    : easyBotOnline
                      ? `已连接 · ${formatHeartbeatAge(easyBot.ageMs)}`
                      : easyBot?.lastSeenAt
                        ? `心跳超时 · ${formatHeartbeatAge(easyBot.ageMs)}`
                        : "未连接"}
                </Tag>
              ),
              children: (
                <div className="connection-service-panel" data-service-panel="easybot">
                  <Alert
                    className="easybot-deployment-alert"
                    type="info"
                    showIcon
                    title="支持网页与 EasyBot 分离部署"
                    description="连接方向是 EasyBot 服务器主动请求网页服务器上的 API。无需在网页中填写 EasyBot 服务器 IP；请在下方填写 EasyBot 服务器能够访问到的 API 公网域名或内网地址。"
                  />
                  <Descriptions
                    className="connection-details connection-details--service"
                    bordered
                    size="small"
                    column={1}
                    items={[
                      {
                        key: "status",
                        label: "实时状态",
                        children: (
                          <Badge
                            status={easyBotOnline ? "success" : "error"}
                            text={easyBotOnline ? "心跳正常" : "未连接或心跳超时"}
                          />
                        )
                      },
                      {
                        key: "heartbeat",
                        label: "最后心跳",
                        children: easyBot?.lastSeenAt
                          ? dayjs(easyBot.lastSeenAt).format("YYYY-MM-DD HH:mm:ss")
                          : "尚未收到"
                      },
                      {
                        key: "heartbeat-age",
                        label: "心跳距今",
                        children: formatHeartbeatAge(easyBot?.ageMs)
                      },
                      {
                        key: "threshold",
                        label: "离线判定",
                        children: easyBot?.healthyWithinMs
                          ? `${Math.round(easyBot.healthyWithinMs / 1_000)} 秒内无心跳`
                          : "—"
                      },
                      {
                        key: "source",
                        label: "检测来源",
                        children: "已鉴权的 EasyBot 处罚队列轮询"
                      },
                      {
                        key: "direction",
                        label: "连接方向",
                        children: "EasyBot 服务器 → NavigationPlan API"
                      }
                    ]}
                  />
                  <div className="connection-detail-note">
                    <RobotOutlined />
                    <div>
                      <strong>状态来自真实 EasyBot 心跳</strong>
                      <p>每次插件通过内部服务令牌轮询 API 时刷新，超过离线阈值后会自动显示为未连接。</p>
                    </div>
                  </div>

                  <div className="connection-form-heading easybot-deployment-heading">
                    <div>
                      <strong>跨服务器接入设置</strong>
                      <p>这些参数对应 EasyBot 动态配置中的 api_base、timeout_ms 与 action_poll_ms。</p>
                    </div>
                  </div>

                  <Form<EasyBotDeploymentSettings>
                    form={easyBotForm}
                    layout="vertical"
                    initialValues={easyBotDeployment}
                    requiredMark={false}
                  >
                    <Form.Item
                      name="apiBase"
                      label="EasyBot 端使用的 API 地址"
                      extra="必须是 EasyBot 所在服务器能够访问的地址；分离部署时不要填写 127.0.0.1。"
                      rules={[
                        { required: true, message: "请输入 EasyBot 能够访问的 API 地址" },
                        {
                          validator: async (_, value: string) => {
                            try {
                              normalizeApiBase(value ?? "");
                            } catch (error) {
                              throw new Error(error instanceof Error ? error.message : "API 地址格式不正确");
                            }
                          }
                        }
                      ]}
                    >
                      <Input
                        prefix={<CloudServerOutlined />}
                        placeholder="https://api.example.com"
                        allowClear
                      />
                    </Form.Item>
                    <div className="connection-number-grid">
                      <Form.Item
                        name="timeoutSeconds"
                        label="外部调用超时"
                        rules={[{ required: true, message: "请输入超时时间" }]}
                      >
                        <InputNumber min={1} max={30} suffix="秒" style={{ width: "100%" }} />
                      </Form.Item>
                      <Form.Item
                        name="pollIntervalSeconds"
                        label="动作轮询间隔"
                        rules={[{ required: true, message: "请输入轮询间隔" }]}
                      >
                        <InputNumber min={5} max={120} suffix="秒" style={{ width: "100%" }} />
                      </Form.Item>
                    </div>
                    <div className="connection-form-actions connection-form-actions--wrap">
                      <Button
                        icon={<ReloadOutlined />}
                        loading={easyBotTesting}
                        onClick={() => void testEasyBotDeployment()}
                      >
                        测试 API 地址
                      </Button>
                      <Button
                        icon={<CopyOutlined />}
                        onClick={() => void copyEasyBotDeployment()}
                      >
                        复制 EasyBot 配置
                      </Button>
                      <Button
                        type="primary"
                        icon={<SaveOutlined />}
                        onClick={() => void saveEasyBotDeployment()}
                      >
                        保存部署参数
                      </Button>
                    </div>
                    <p className="connection-form-note">
                      <ClockCircleOutlined /> 网页无法直接修改另一台服务器上的 EasyBot 配置；保存后仍需把复制内容填写到 EasyBot WebUI 的“领航计划”动态配置中。
                    </p>
                  </Form>
                </div>
              )
            },
            {
              key: "game",
              label: (
                <span className="connection-service-label">
                  <CloudServerOutlined />
                  <strong>Folia 游戏桥接</strong>
                </span>
              ),
              extra: (
                <Tag color={gameOnline ? "success" : health.phase === "checking" ? "processing" : "error"}>
                  {health.phase === "checking"
                    ? "检测中"
                    : gameOnline
                      ? `已连接 · ${formatHeartbeatAge(game?.ageMs)}`
                      : game?.lastSeenAt
                        ? `心跳超时 · ${formatHeartbeatAge(game?.ageMs)}`
                        : "未连接"}
                </Tag>
              ),
              children: (
                <div className="connection-service-panel" data-service-panel="game">
                  <Alert
                    type="info"
                    showIcon
                    title="状态来自已鉴权的 Folia 插件真实桥接心跳"
                    description="该状态不是网页探测端口：只有实际加载的领航计划插件调用内部 API 后才会显示为已连接。"
                  />
                  <Descriptions
                    className="connection-details connection-details--service"
                    bordered
                    size="small"
                    column={1}
                    items={[
                      {
                        key: "status",
                        label: "实时状态",
                        children: <Badge status={gameOnline ? "success" : "error"} text={gameOnline ? "桥接心跳正常" : "未连接或心跳超时"} />
                      },
                      {
                        key: "heartbeat",
                        label: "最后心跳",
                        children: game?.lastSeenAt ? dayjs(game.lastSeenAt).format("YYYY-MM-DD HH:mm:ss") : "尚未收到"
                      },
                      {
                        key: "age",
                        label: "心跳距今",
                        children: formatHeartbeatAge(game?.ageMs)
                      },
                      {
                        key: "server",
                        label: "服务器标识",
                        children: String(gameMetadata.serverId ?? "未上报")
                      },
                      {
                        key: "plugin",
                        label: "插件版本",
                        children: String(gameMetadata.pluginVersion ?? "未上报")
                      },
                      {
                        key: "folia",
                        label: "Folia 调度",
                        children: gameMetadata.folia === true ? "已启用" : gameMetadata.folia === false ? "未检测到" : "未上报"
                      },
                      {
                        key: "znpcs",
                        label: "ZNPCsPlus",
                        children: String(gameMetadata.znpcsVersion ?? "未加载或未上报")
                      },
                      {
                        key: "threshold",
                        label: "离线判定",
                        children: game?.healthyWithinMs ? `${Math.round(game.healthyWithinMs / 1_000)} 秒内无心跳` : "—"
                      }
                    ]}
                  />
                </div>
              )
            }
          ]}
        />
      </Drawer>
    </>
  );
}
